import type { ToolBadgeSpec } from '../types.js';

import { getPlaceIcon } from '#lib/place-category.js';

import {
  queryContextLabel,
  readArray,
  readField,
  readString,
  truncateForLabel,
  withQueryLabel,
} from '../extractors.js';

function extractPlaces(output: unknown) {
  const arr = readArray(readField(output, 'places'));
  if (!arr) return undefined;
  const items = [];
  for (const [index, entry] of arr.entries()) {
    const id = readString(readField(entry, 'id'));
    if (!id) continue;
    const title = readString(readField(entry, 'name')) ?? `Result ${index + 1}`;
    const address = readField(entry, 'address');
    const addressParts = [
      readString(readField(address, 'line1')),
      readString(readField(address, 'city')),
      readString(readField(address, 'country')),
    ].filter((p): p is string => !!p);
    const shortAddress = addressParts.length > 0 ? addressParts.join(', ') : undefined;
    const subtitle =
      shortAddress ??
      readString(readField(entry, 'formattedAddress')) ??
      readString(readField(entry, 'formatted_address'));
    const primaryType =
      readString(readField(entry, 'primaryType')) ??
      readString(readField(entry, 'primary_type')) ??
      readArray(readField(entry, 'types'))?.find((t): t is string => typeof t === 'string');
    items.push({
      key: `${id}-${index}`,
      title,
      subtitle,
      previewType: 'place' as const,
      id,
      iconName: getPlaceIcon(primaryType),
    });
  }
  return { items };
}

function getPlaceQuery(toolCall: { input?: Record<string, unknown> }): string | undefined {
  return readString(readField(toolCall.input, 'query'));
}

function getNearbyType(toolCall: { input?: Record<string, unknown> }): string | undefined {
  const type = readString(readField(toolCall.input, 'type'));
  if (!type) return undefined;
  return truncateForLabel(type.replaceAll('_', ' '));
}

function nearbyNoun(type: string): { singular: string; plural: string } {
  return { singular: type, plural: `${type}s` };
}

export const fetchPlaceDataSpec: ToolBadgeSpec = {
  toolName: 'fetchPlaceData',
  icon: 'map_search',
  label: (toolCall, active) => withQueryLabel(active ? 'Searching for' : 'Searched for', getPlaceQuery(toolCall)),
  activitySummary: {
    key: 'placeSearch',
    label: (_, active) => (active ? 'Searching for places' : 'Searched for places'),
  },
  resultNoun: { singular: 'place', plural: 'places' },
  dataSource: 'Google Places',
  extractResults: (toolCall) => {
    const group = extractPlaces(toolCall.output);
    if (!group) return undefined;
    return { ...group, contextLabel: queryContextLabel(getPlaceQuery(toolCall)) };
  },
};

export const fetchPlacesNearbySpec: ToolBadgeSpec = {
  toolName: 'fetchPlacesNearby',
  icon: 'map_search',
  label: (toolCall, active) => {
    const type = getNearbyType(toolCall);
    if (type) return active ? `Searching nearby for ${type}s` : `Searched nearby for ${type}s`;
    return active ? 'Searching for places nearby' : 'Searched for places nearby';
  },
  activitySummary: {
    key: 'placeSearch',
    label: (_, active) => (active ? 'Searching for places' : 'Searched for places'),
  },
  resultNoun: { singular: 'place', plural: 'places' },
  dataSource: 'Google Places',
  extractResults: (toolCall) => {
    const group = extractPlaces(toolCall.output);
    if (!group) return undefined;
    const type = getNearbyType(toolCall);
    return { ...group, contextLabel: 'nearby', ...(type && { resultNoun: nearbyNoun(type) }) };
  },
};
